// frontend/src/pages/technician/TechnicianSchedule.js

import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    Paper,
    Alert,
    CircularProgress,
    Chip,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    TextField,
    Divider,
    Avatar,
    Tab,
    Tabs
} from '@mui/material';
// Importar nuestro Timeline personalizado
import {
    Timeline,
    TimelineItem,
    TimelineSeparator,
    TimelineConnector,
    TimelineContent,
    TimelineDot,
    TimelineOppositeContent
} from '../../components/CustomTimeline';
import {
    Schedule,
    Assignment,
    Build,
    LocationOn,
    Phone,
    Navigation,
    PlayArrow,
    Stop,
    AccessTime,
    Event,
    Today,
    CalendarMonth,
    Refresh,
    Route,
    Warning,
    CheckCircle,
    Person
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { addDays, format, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay } from 'date-fns';
import { technicianApiService } from '../../services/technicianApiService';

const TechnicianSchedule = () => {
    const [schedule, setSchedule] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentTab, setCurrentTab] = useState(0);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [weekDates, setWeekDates] = useState([]);
    const [dailyTasks, setDailyTasks] = useState([]);
    const [routeOptimized, setRouteOptimized] = useState(false);
    const [error, setError] = useState(null);
    const [workSession, setWorkSession] = useState(null);

    useEffect(() => {
        loadSchedule();
        generateWeekDates();
        checkWorkSession();
    }, [selectedDate]);

    const generateWeekDates = () => {
        const start = startOfWeek(selectedDate, { weekStartsOn: 1 }); // Lunes
        const end = endOfWeek(selectedDate, { weekStartsOn: 1 });
        const dates = eachDayOfInterval({ start, end });
        setWeekDates(dates);
    };

    const loadSchedule = async () => {
        try {
            setLoading(true);
            setError(null);

            const startDate = format(startOfWeek(selectedDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
            const endDate = format(endOfWeek(selectedDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');

            const data = await technicianApiService.getSchedule(startDate, endDate);
            const scheduleData = data.schedule || [];
            setSchedule(scheduleData);

            // Filtrar tareas del día seleccionado
            const dayTasks = scheduleData.filter(task => {
                if (!task.date) return false;
                return isSameDay(new Date(task.date), selectedDate);
            });
            setDailyTasks(dayTasks);

        } catch (error) {
            console.error('Error cargando horario:', error);
            setError(error.response?.data?.message || 'Error cargando horario');
            setSchedule([]);
            setDailyTasks([]);
        } finally {
            setLoading(false);
        }
    };

    const checkWorkSession = () => {
        const session = localStorage.getItem('activeWorkSession');
        setWorkSession(session ? JSON.parse(session) : null);
    };

    const optimizeRoute = async () => {
        try {
            const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
            const route = await technicianApiService.getTodayRoute(selectedDateStr);
            setRouteOptimized(true);
            console.log('Ruta optimizada:', route);

            // Aquí podrías mostrar un diálogo con la ruta optimizada
            alert('Ruta optimizada generada. Revisa la consola para más detalles.');
        } catch (error) {
            console.error('Error optimizando ruta:', error);
            setError(error.response?.data?.message || 'Error optimizando ruta');
        }
    };

    const startWorkSession = async () => {
        try {
            const session = await technicianApiService.startWorkSession('Inicio de jornada');
            localStorage.setItem('activeWorkSession', JSON.stringify(session));
            setWorkSession(session);
        } catch (error) {
            console.error('Error iniciando sesión:', error);
            setError(error.response?.data?.message || 'Error iniciando sesión de trabajo');
        }
    };

    const endWorkSession = async () => {
        try {
            await technicianApiService.endWorkSession('Fin de jornada');
            localStorage.removeItem('activeWorkSession');
            setWorkSession(null);
        } catch (error) {
            console.error('Error finalizando sesión:', error);
            setError(error.response?.data?.message || 'Error finalizando sesión de trabajo');
        }
    };

    const getTaskIcon = (type) => {
        switch (type) {
            case 'installation': return <Assignment />;
            case 'support': return <Build />;
            case 'maintenance': return <Build />;
            default: return <Event />;
        }
    };

    const getTaskColor = (type) => {
        switch (type) {
            case 'installation': return 'primary';
            case 'support': return 'warning';
            case 'maintenance': return 'info';
            default: return 'grey';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'assigned': return 'warning';
            case 'in_progress': return 'info';
            case 'completed': return 'success';
            case 'cancelled': return 'error';
            default: return 'default';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'assigned': return 'Asignado';
            case 'in_progress': return 'En Progreso';
            case 'completed': return 'Completado';
            case 'cancelled': return 'Cancelado';
            default: return status;
        }
    };

    const getTasksForDate = (date) => {
        if (!Array.isArray(schedule)) return [];
        return schedule.filter(task => {
            if (!task.date) return false;
            return isSameDay(new Date(task.date), date);
        });
    };

    const getTotalTasksForDate = (date) => {
        return getTasksForDate(date).length;
    };

    const getCompletedTasksForDate = (date) => {
        return getTasksForDate(date).filter(task => task.status === 'completed').length;
    };

    const makePhoneCall = (phoneNumber) => {
        if (phoneNumber) {
            window.open(`tel:${phoneNumber}`);
        }
    };

    const openNavigation = (coordinates) => {
        if (coordinates) {
            const coords = coordinates.replace(/[()]/g, '').split(',');
            if (coords.length === 2) {
                const val1 = parseFloat(coords[0].trim());
                const val2 = parseFloat(coords[1].trim());
                let lat = val1;
                let lng = val2;
                if (Math.abs(val1) > 90 && Math.abs(val2) <= 90) {
                    lat = val2;
                    lng = val1;
                }
                window.open(`https://maps.google.com/maps?q=${lat},${lng}`, '_blank');
            }
        }
    };

    const refreshData = async () => {
        await loadSchedule();
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h5" gutterBottom>
                            Mi Horario de Trabajo
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Gestiona tu agenda y optimiza tus rutas diarias
                        </Typography>
                    </Box>
                    <Box display="flex" gap={1} flexWrap="wrap">
                        {workSession ? (
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<Stop />}
                                onClick={endWorkSession}
                            >
                                Finalizar Jornada
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<PlayArrow />}
                                onClick={startWorkSession}
                            >
                                Iniciar Jornada
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            startIcon={<Route />}
                            onClick={optimizeRoute}
                            disabled={dailyTasks.length === 0}
                        >
                            Optimizar Ruta
                        </Button>
                        <IconButton onClick={refreshData}>
                            <Refresh />
                        </IconButton>
                    </Box>
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
                    <Tab label="Vista Diaria" icon={<Today />} />
                    <Tab label="Vista Semanal" icon={<CalendarMonth />} />
                </Tabs>
            </Paper>

            {/* Vista Diaria */}
            {currentTab === 0 && (
                <Grid container spacing={3}>
                    {/* Selector de fecha */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 2 }}>
                            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                                    <DatePicker
                                        label="Seleccionar fecha"
                                        value={selectedDate}
                                        onChange={(date) => setSelectedDate(date)}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                sx: { minWidth: 200 }
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                                <Typography variant="h6">
                                    {format(selectedDate, "EEEE, d 'de' MMMM", { locale: es })}
                                </Typography>
                                <Chip
                                    label={`${dailyTasks.length} tarea${dailyTasks.length !== 1 ? 's' : ''}`}
                                    color="primary"
                                />
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Timeline del día */}
                    <Grid item xs={12} md={8}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Agenda del Día
                                </Typography>

                                {dailyTasks.length > 0 ? (
                                    <Timeline>
                                        {dailyTasks.map((task, index) => (
                                            <TimelineItem key={task.id || index}>
                                                <TimelineOppositeContent color="textSecondary">
                                                    {task.estimated_time || '09:00 AM'}
                                                </TimelineOppositeContent>
                                                <TimelineSeparator>
                                                    <TimelineDot color={getTaskColor(task.type)}>
                                                        {getTaskIcon(task.type)}
                                                    </TimelineDot>
                                                    {index < dailyTasks.length - 1 && <TimelineConnector />}
                                                </TimelineSeparator>
                                                <TimelineContent>
                                                    <Card variant="outlined" sx={{ mb: 1 }}>
                                                        <CardContent sx={{ p: 2 }}>
                                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                                                <Box>
                                                                    <Typography variant="subtitle1">
                                                                        {task.client_name || 'Cliente no especificado'}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="textSecondary">
                                                                        {task.task_type || task.type}
                                                                    </Typography>
                                                                    <Typography variant="body2">
                                                                        📍 {task.address || 'Dirección no especificada'}
                                                                    </Typography>
                                                                    {task.notes && (
                                                                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                                                            📝 {task.notes}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                                <Box display="flex" flexDirection="column" gap={1}>
                                                                    <Chip
                                                                        label={getStatusText(task.status)}
                                                                        color={getStatusColor(task.status)}
                                                                        size="small"
                                                                    />
                                                                    <Box display="flex" gap={1}>
                                                                        {task.phone && (
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => makePhoneCall(task.phone)}
                                                                                title="Llamar"
                                                                            >
                                                                                <Phone />
                                                                            </IconButton>
                                                                        )}
                                                                        {task.coordinates && (
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => openNavigation(task.coordinates)}
                                                                                title="Navegar"
                                                                            >
                                                                                <Navigation />
                                                                            </IconButton>
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                </TimelineContent>
                                            </TimelineItem>
                                        ))}
                                    </Timeline>
                                ) : (
                                    <Alert severity="info">
                                        No hay tareas programadas para esta fecha
                                    </Alert>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Panel lateral con resumen */}
                    <Grid item xs={12} md={4}>
                        <Grid container spacing={2}>
                            {/* Estadísticas del día */}
                            <Grid item xs={12}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Resumen del Día
                                        </Typography>
                                        <List dense>
                                            <ListItem>
                                                <ListItemIcon><Assignment /></ListItemIcon>
                                                <ListItemText
                                                    primary="Total de tareas"
                                                    secondary={dailyTasks.length}
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon><CheckCircle /></ListItemIcon>
                                                <ListItemText
                                                    primary="Completadas"
                                                    secondary={dailyTasks.filter(t => t.status === 'completed').length}
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon><Warning /></ListItemIcon>
                                                <ListItemText
                                                    primary="Pendientes"
                                                    secondary={dailyTasks.filter(t => t.status === 'assigned').length}
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon><AccessTime /></ListItemIcon>
                                                <ListItemText
                                                    primary="Tiempo estimado"
                                                    secondary={`${dailyTasks.length * 90} min`}
                                                />
                                            </ListItem>
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Información adicional */}
                            <Grid item xs={12}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Información Útil
                                        </Typography>
                                        <Typography variant="body2" gutterBottom>
                                            <strong>Clima:</strong> Soleado, 25°C
                                        </Typography>
                                        <Typography variant="body2" gutterBottom>
                                            <strong>Tráfico:</strong> Moderado
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Combustible:</strong> Recomendado llenar tanque
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            )}

            {/* Vista Semanal */}
            {currentTab === 1 && (
                <Grid container spacing={2}>
                    {weekDates.map((date) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={date.toISOString()}>
                            <Card
                                sx={{
                                    height: '100%',
                                    border: isSameDay(date, selectedDate) ? 2 : 0,
                                    borderColor: 'primary.main',
                                    cursor: 'pointer'
                                }}
                                onClick={() => setSelectedDate(date)}
                            >
                                <CardContent>
                                    <Box textAlign="center" mb={2}>
                                        <Typography variant="h6">
                                            {format(date, 'EEEE', { locale: es })}
                                        </Typography>
                                        <Typography variant="h4" color="primary">
                                            {format(date, 'd')}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {format(date, 'MMMM', { locale: es })}
                                        </Typography>
                                    </Box>

                                    <Divider sx={{ mb: 2 }} />

                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="body2">
                                            Total de tareas
                                        </Typography>
                                        <Chip
                                            label={getTotalTasksForDate(date)}
                                            size="small"
                                            color={getTotalTasksForDate(date) > 0 ? 'primary' : 'default'}
                                        />
                                    </Box>

                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body2">
                                            Completadas
                                        </Typography>
                                        <Chip
                                            label={getCompletedTasksForDate(date)}
                                            size="small"
                                            color={getCompletedTasksForDate(date) > 0 ? 'success' : 'default'}
                                        />
                                    </Box>

                                    {/* Pequeña lista de tareas */}
                                    {getTasksForDate(date).slice(0, 3).map((task, index) => (
                                        <Box key={task.id || index} display="flex" alignItems="center" mt={1}>
                                            <Avatar sx={{ width: 24, height: 24, mr: 1 }}>
                                                {getTaskIcon(task.type)}
                                            </Avatar>
                                            <Typography variant="caption" noWrap>
                                                {task.client_name || 'Cliente no especificado'}
                                            </Typography>
                                        </Box>
                                    ))}

                                    {getTasksForDate(date).length > 3 && (
                                        <Typography variant="caption" color="textSecondary">
                                            +{getTasksForDate(date).length - 3} más...
                                        </Typography>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
};

export default TechnicianSchedule;